import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { formatSGD, waLink } from "../lib/mockData";
import { useProducts, useCategories, useSettings } from "../hooks/useCatalog";
import { useSeo } from "../lib/seo";
import ProductCard from "../components/common/ProductCard";
import { SectionLabel } from "../components/common/Ornaments";

const SORTS = [
  { value: "latest", label: "Latest" },
  { value: "popular", label: "Popular" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: categories = [] } = useCategories();
  const { data: settings } = useSettings();
  const freeDeliveryThreshold = settings?.freeDeliveryThreshold ?? 200;
  useSeo({ pageKey: "shop" });
  const [search, setSearch] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("cat") || "all");
  const [sort, setSort] = useState(params.get("sort") || "latest");
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(400);
  const [showFilters, setShowFilters] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [bestSellerOnly, setBestSellerOnly] = useState(false);
  const [newArrivalOnly, setNewArrivalOnly] = useState(false);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== "all") list = list.filter((p) => p.category === category);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.shortDesc.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q)
      );
    }
    if (featuredOnly) list = list.filter((p) => p.featured);
    if (bestSellerOnly) list = list.filter((p) => p.bestSeller);
    if (newArrivalOnly) list = list.filter((p) => p.newArrival);
    list = list.filter((p) => {
      const effectivePrice = p.discountPrice ?? p.price;
      return effectivePrice >= priceMin && effectivePrice <= priceMax;
    });
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => (a.discountPrice ?? a.price) - (b.discountPrice ?? b.price));
        break;
      case "price-desc":
        list.sort((a, b) => (b.discountPrice ?? b.price) - (a.discountPrice ?? a.price));
        break;
      case "popular":
        list.sort((a, b) => Number(b.bestSeller) - Number(a.bestSeller));
        break;
      default:
        break;
    }
    return list;
  }, [products, category, search, sort, priceMin, priceMax, featuredOnly, bestSellerOnly, newArrivalOnly]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value && value !== "all") p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  return (
    <div data-testid="shop-page">
      {/* Header */}
      <section className="bg-emerald text-cream py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] islamic-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionLabel><span>Our Collection</span></SectionLabel>
          <h1 className="font-serif text-5xl md:text-6xl mt-4">
            Shop All <span className="italic text-gold">Hampers</span>
          </h1>
          <p className="mt-4 text-cream/80 max-w-xl mx-auto">
            Premium halal-friendly gift hampers, curated for every occasion.
          </p>
        </div>
      </section>

      {/* Promo banner */}
      <div className="bg-gold text-emerald py-3 text-center text-sm font-medium">
        <span className="uppercase tracking-widest text-xs">Special:</span>{" "}
        Free islandwide delivery for orders above {formatSGD(freeDeliveryThreshold)}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald/60" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                updateParam("q", e.target.value);
              }}
              placeholder="Search hampers..."
              className="w-full pl-11 pr-4 py-3 bg-white border border-gold/30 rounded-lg text-sm focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
              data-testid="shop-search-input"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              updateParam("sort", e.target.value);
            }}
            className="bg-white border border-gold/30 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-gold outline-none"
            data-testid="shop-sort-select"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>Sort: {s.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden inline-flex items-center gap-2 border border-gold/40 px-4 py-3 text-sm text-emerald rounded-lg"
            data-testid="shop-filter-toggle"
          >
            <SlidersHorizontal size={16} />
            Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
          {/* Filters */}
          <aside className={`${showFilters ? "block" : "hidden"} md:block`}>
            <div className="bg-white border border-gold/25 rounded-xl p-5 sticky top-32">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl text-emerald">Filters</h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="md:hidden text-emerald/60"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mb-6">
                <label className="text-xs uppercase tracking-widest text-emerald-light/70 font-medium">
                  Category
                </label>
                <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  <button
                    onClick={() => {
                      setCategory("all");
                      updateParam("cat", "all");
                    }}
                    className={`block w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                      category === "all"
                        ? "bg-emerald text-cream"
                        : "text-emerald hover:bg-emerald/5"
                    }`}
                    data-testid="filter-cat-all"
                  >
                    All Categories
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.slug}
                      onClick={() => {
                        setCategory(c.slug);
                        updateParam("cat", c.slug);
                      }}
                      className={`block w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                        category === c.slug
                          ? "bg-emerald text-cream"
                          : "text-emerald hover:bg-emerald/5"
                      }`}
                      data-testid={`filter-cat-${c.slug}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="text-xs uppercase tracking-widest text-emerald-light/70 font-medium">
                  Show
                </label>
                <div className="mt-3 space-y-1.5">
                  <button
                    onClick={() => setFeaturedOnly((v) => !v)}
                    className={`block w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                      featuredOnly
                        ? "bg-emerald text-cream"
                        : "text-emerald hover:bg-emerald/5"
                    }`}
                    data-testid="filter-featured"
                  >
                    Featured
                  </button>
                  <button
                    onClick={() => setBestSellerOnly((v) => !v)}
                    className={`block w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                      bestSellerOnly
                        ? "bg-emerald text-cream"
                        : "text-emerald hover:bg-emerald/5"
                    }`}
                    data-testid="filter-bestseller"
                  >
                    Best Sellers
                  </button>
                  <button
                    onClick={() => setNewArrivalOnly((v) => !v)}
                    className={`block w-full text-left text-sm px-3 py-2 rounded transition-colors ${
                      newArrivalOnly
                        ? "bg-emerald text-cream"
                        : "text-emerald hover:bg-emerald/5"
                    }`}
                    data-testid="filter-newarrival"
                  >
                    New Arrivals
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-emerald-light/70 font-medium">
                  Price Range
                </label>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={priceMax}
                    step={10}
                    value={priceMin}
                    onChange={(e) => setPriceMin(Number(e.target.value))}
                    className="w-full bg-white border border-gold/30 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
                    data-testid="filter-price-min"
                    aria-label="Minimum price"
                  />
                  <span className="text-emerald/60 text-sm">–</span>
                  <input
                    type="number"
                    min={priceMin}
                    max={400}
                    step={10}
                    value={priceMax}
                    onChange={(e) => setPriceMax(Number(e.target.value))}
                    className="w-full bg-white border border-gold/30 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
                    data-testid="filter-price-range"
                    aria-label="Maximum price"
                  />
                </div>
                <div className="text-sm text-emerald mt-2">
                  <span className="font-semibold">{formatSGD(priceMin)}</span> –{" "}
                  <span className="font-semibold">{formatSGD(priceMax)}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Products */}
          <div>
            <div className="text-sm text-emerald-light/70 mb-5" data-testid="shop-results-count">
              Showing <span className="font-semibold text-emerald">{filtered.length}</span> hampers
            </div>
            {productsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gold/25 rounded-xl overflow-hidden animate-pulse"
                  >
                    <div className="aspect-square bg-emerald/10" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-emerald/10 rounded w-3/4" />
                      <div className="h-3 bg-emerald/10 rounded w-1/2" />
                      <div className="h-4 bg-gold/20 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-gold/25 rounded-xl p-10 text-center">
                <div className="font-serif text-2xl text-emerald">No hampers match your search</div>
                <p className="text-emerald-light/70 mt-2">Try adjusting filters or reach out for a custom hamper.</p>
                <a
                  href={waLink("Hi HALALGIFHAMPERS, I'd like a custom hamper suggestion.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 bg-emerald text-cream px-5 py-2.5 text-sm"
                >
                  Ask on WhatsApp
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
